'use client'

import { clsx } from 'clsx'
import {
  ChevronLeft,
  ChevronRight,
  CircleAlert,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import Betted from '@/components/icons/betted.svg'
import Hourglass from '@/components/icons/hourglass.svg'
import Nickname from '@/components/nickname'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  formatNumber,
  formatPercent,
  formatPrice,
  formatRatio,
  formatTime,
} from '@/lib/formatter'
import { cn } from '@/lib/utils'
import {
  useReadCompetitionRankingQuery,
  useReadOneCompetitionQuery,
} from '@/redux/competition'
import { useReadBettingRecordsQuery } from '@/redux/competition-betting'
import { RelatedSimpleUser } from '@/redux/user'
import { useAppSelector } from '@/redux/utils'

import BetDialog from './bet-dialog'
import RedeemDialog from './redeem-dialog'

export interface RankingProps {
  id: number
  mineUserId: number | undefined
}

const size = 15

export default function Ranking({ id, mineUserId }: RankingProps) {
  const [page, setPage] = useState(1)
  const [pageChanged, setPageChanged] = useState(false)
  const [betDialogOpen, setBetDialogOpen] = useState(false)
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false)
  const token = useAppSelector((state) => state.global.token)
  const { data: competition } = useReadOneCompetitionQuery(id)
  const { data: rankings } = useReadCompetitionRankingQuery({
    id,
    page: pageChanged ? page : undefined,
    size,
    showMinePage: token !== undefined && !pageChanged,
  })
  const { data: bettingRecords } = useReadBettingRecordsQuery(id, {
    skip: token === undefined,
  })
  const totalPage = rankings ? Math.ceil(rankings.total / size) : 0
  const onGoPrevPage = useCallback(() => {
    setPage((currentPage) => (currentPage > 1 ? currentPage - 1 : currentPage))
    setPageChanged(true)
  }, [])
  const onGoNextPage = useCallback(() => {
    setPage((currentPage) =>
      currentPage < totalPage ? currentPage + 1 : currentPage,
    )
    setPageChanged(true)
  }, [totalPage])

  // 让bettingClosesIn随时间变化
  const [now, setNow] = useState(0)

  //const [competitor, setCompetitor] = useState<RelatedSimpleUser>(null);

  const [betData, setBetData] = useState<{
    competitor: RelatedSimpleUser
  }>({
    competitor: null,
  })

  const [redeemData, setRedeemData] = useState<{
    competitor: RelatedSimpleUser
    maxRedeemAmount: number
    myBet: number
  }>({
    competitor: null,
    maxRedeemAmount: 0,
    myBet: 0,
  })

  // 让bettingClosesIn随时间变化
  useEffect(() => {
    // 初始化当前时间
    const timer = setInterval(() => {
      setNow(Date.now())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const bettingClosesIn = useMemo(() => {
    if (!competition) return null
    const closesAt = competition.competitionEndAt
    const diff = closesAt - now
    if (diff <= 0) {
      return '00:00:00'
    }
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)
    // 补零格式化
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  }, [competition, now])

  const bet = (data: { competitor: RelatedSimpleUser }) => {
    setBetData(data)
    setBetDialogOpen(true)
  }

  const redeem = (data: {
    competitor: RelatedSimpleUser
    maxRedeemAmount: number
    myBet: number
  }) => {
    setRedeemData(data)
    setRedeemDialogOpen(true)
  }

  return (
    <div className="flex-1">
      <div>
        <div className="flex justify-between items-end">
          <div>
            <div className="text-2xl font-semibold mb-2 flex items-end gap-2">
              Ranking
              <div className="text-sm leading-5.5 text-tertiary-foreground font-normal flex items-center gap-1">
                Redemption weights are dynamically adjusted based on rankings
                <CircleAlert className="size-3 text-tertiary-foreground"/>
              </div>

            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-tertiary-foreground">
                Update time: {formatTime('MM/DD HH:mm:ss')(rankings?.updatedAt)}
              </div>
              <div className="h-3 border border-[#FFFFFF1F]"></div>
              <div className="flex items-center gap-2 text-sm text-tertiary-foreground">
                <Hourglass />
                {competition?.isBetting ? (
                  <span>Subscribing closes in {bettingClosesIn}</span>
                ) : (
                  <span>Subscribing for this competition will open soon.</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2 items-center bg-white/8 rounded-full">
            <ChevronLeft
              className={clsx(
                page > 1
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed stroke-disabled-foreground',
                'size-8 p-1',
              )}
              onClick={onGoPrevPage}
            />
            <div>
              {page} / {totalPage}
            </div>
            <ChevronRight
              className={clsx(
                page < totalPage
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed stroke-disabled-foreground',
                'size-8 p-1',
              )}
              onClick={onGoNextPage}
            />
          </div>
        </div>

        <Table className="mt-6 border-spacing-y-1">
          <TableHeader>
            <TableRow className="text-secondary-foreground *:text-xs *:align-bottom">
              <TableHead></TableHead>
              <TableHead>Participant</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead>P&L</TableHead>
              <TableHead>ROI</TableHead>
              {/* <TableHead>Calmar Ratio</TableHead> */}
              {/* <TableHead>Sharpe Ratio</TableHead> */}
              <TableHead>Sortino Ratio</TableHead>
              {/* <TableHead>Omega Ratio</TableHead> */}
              <TableHead>
                Total points
                <br />
                subscribed
              </TableHead>
              <TableHead>
                My
                <br />
                subscription
              </TableHead>
              <TableHead>Operation</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rankings?.rows.map(
              (
                {
                  ranking,
                  user,
                  sortinoRatio,
                  totalBet,
                  myBet,
                  liveRedeem,
                  balance,
                  roi,
                  pnl,
                },
                index,
              ) => (
                <TableRow
                  className={clsx(
                    'bg-background-tertiary text-xs',
                    user &&
                      user?.id === mineUserId &&
                      '*:border-y *:border-primary *:first:border-l *:last:border-r',
                  )}
                  key={index}
                  rounded
                >
                  <TableCell className="font-poly-sans text-xl px-0 w-10 flex items-center gap-4.5 ">
                    {bettingRecords?.find(
                      (record) => record.competitorID === user?.id,
                    ) === undefined ? (
                      <div className="min-w-2.25"></div>
                    ) : (
                      <Betted className="min-w-2.25"></Betted>
                    )}
                    <div>{ranking}</div>
                  </TableCell>
                  <TableCell>
                    <Nickname user={user} />
                  </TableCell>
                  <TableCell>{formatPrice()(balance)}</TableCell>
                  <TableCell className={cn(pnl < 0 && 'text-destructive')}>
                    {formatPrice()(pnl)}
                  </TableCell>
                  <TableCell>{formatPercent()(roi)}</TableCell>
                  <TableCell>{formatRatio()(sortinoRatio)}</TableCell>
                  <TableCell>{formatNumber()(totalBet)} pts</TableCell>
                  <TableCell>{formatNumber()(myBet)} pts</TableCell>
                  <TableCell>
                    <div className="flex gap-3">
                      <button
                        className="text-xs text-primary disabled:text-primary-disabled cursor-pointer disabled:cursor-not-allowed"
                        disabled={
                          !(
                            competition &&
                            competition.isBetting &&
                            competition.competitionEndAt - 1800000 > now
                          )
                        }
                        onClick={() => {
                          bet({ competitor: user })
                        }}
                      >
                        Subscribe
                      </button>
                      <Separator
                        orientation="vertical"
                        className="bg-white/8"
                      />
                      <button
                        className="text-xs text-primary disabled:text-primary-disabled cursor-pointer disabled:cursor-not-allowed"
                        disabled={
                          !(
                            competition &&
                            competition.isBetting &&
                            competition.competitionEndAt > now &&
                            liveRedeem > 0
                          )
                        }
                        onClick={() => {
                          redeem({
                            competitor: user,
                            maxRedeemAmount: liveRedeem,
                            myBet,
                          })
                        }}
                      >
                        Redeem
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </div>

      <BetDialog
        open={betDialogOpen}
        onOpenChange={setBetDialogOpen}
        competitionId={id}
        competitor={betData.competitor}
      />

      <RedeemDialog
        open={redeemDialogOpen}
        onOpenChange={setRedeemDialogOpen}
        competitionId={id}
        competitor={redeemData.competitor}
        maxRedeemAmount={redeemData.maxRedeemAmount}
        myBet={redeemData.myBet}
      />
    </div>
  )
}
